import React, { useRef, useState } from "react";
import { Upload, X, Crop as CropIcon } from "lucide-react";
import { imgUrl } from "../../lib/api";
import { toast } from "sonner";
import ImageCropper from "./ImageCropper";
import { uploadImage, validateImageFile, UPLOAD_MAX_BYTES } from "../../lib/uploadImage";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

/**
 * Multi-image uploader for admin/seller CMS.
 *
 * When `cropAspect` is set (e.g. 1 for square, 16/9 for banners), each
 * selected file is opened in a cropping modal before upload with that
 * ratio pre-selected. When `cropAspect` is NOT set (typical for product
 * galleries), the modal shows Free aspect and cropping is optional —
 * the user can either "Use original" or fine-tune with a picked ratio.
 *
 * Multi-select is always enabled — the cropper queues files and
 * processes them one at a time, so nothing stops you from picking
 * 12 photos in one go.
 *
 * Every uploaded thumbnail also gets an on-hover "Crop" button so
 * you can re-crop AFTER upload if the original was fine but you now
 * want to tweak framing.
 */
export default function ImageUploader({
  value = [],
  onChange,
  max = 6,
  testid = "image-uploader",
  aspect = "aspect-[3/4]",
  label = "Add image",
  cropAspect,
  allowSkipCrop = true,
  lockAspect = false,
}) {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  // Queue of files awaiting cropping. Populated on file-select when the
  // user WANTS to crop (either because `cropAspect` is set OR because
  // they clicked "Crop with options"). Empty otherwise → direct upload.
  const [cropQueue, setCropQueue] = useState([]);
  // Index of the already-uploaded image the user asked to re-crop.
  // When set, `cropQueue[0]` is a fetched-from-URL File and confirming
  // REPLACES value[recropIndex] instead of appending.
  const [recropIndex, setRecropIndex] = useState(null);

  const uploadOne = async (file) => {
    const data = await uploadImage(file);
    return data.url || data.storage_path;
  };

  const uploadMany = async (files) => {
    setLoading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        try {
          const url = await uploadOne(file);
          uploaded.push(url);
        } catch (err) {
          toast.error(`${file.name || "Image"}: ${err.message}`);
        }
      }
      if (uploaded.length) onChange([...value, ...uploaded]);
      if (uploaded.length && uploaded.length === files.length) {
        toast.success(`Uploaded ${uploaded.length} image(s)`);
      }
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const validate = (files) => {
    if (value.length + files.length > max) {
      toast.error(`Max ${max} images — you already have ${value.length}.`);
      return false;
    }
    for (const f of files) {
      // Cropper flow is limited to browser-decodable formats — HEIC
      // etc. can be uploaded directly but the cropper's canvas can't
      // decode them. Enforce the tighter subset here.
      if (!ALLOWED_TYPES.includes(f.type)) {
        toast.error(`${f.name}: only JPG, PNG, WebP or GIF allowed for the cropper.`);
        return false;
      }
      if (f.size > UPLOAD_MAX_BYTES) {
        const mb = (f.size / (1024 * 1024)).toFixed(1);
        toast.error(`${f.name}: ${mb} MB is too large. Max 10 MB — please compress or crop the image.`);
        return false;
      }
      const pre = validateImageFile(f);
      if (pre) { toast.error(`${f.name}: ${pre}`); return false; }
    }
    return true;
  };

  const onSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!validate(files)) { e.target.value = ""; return; }
    if (cropAspect) {
      // Caller wants EVERY new file to hit the cropper first (e.g. banners
      // that MUST land at 16:9). Queue and reset the input so cancelling
      // out mid-flow lets the user re-select the same file.
      setCropQueue(files);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    // Default path: direct upload of originals. Restores the pre-crop
    // multi-image UX that operators expect for product galleries.
    await uploadMany(files);
  };

  const openCropperForExisting = async (idx) => {
    // Fetch the already-uploaded image and re-open the cropper on it.
    // Uploads REPLACE value[idx] on confirm.
    //
    // S3/CloudFront images can't be `fetch()`-ed directly from the
    // browser without CORS headers on the bucket — the response body
    // is blocked and `.blob()` throws. We route through the backend's
    // `/api/uploads/proxy` endpoint (JWT-scoped, host-allowlisted) so
    // the fetch is same-origin and CORS never enters the picture.
    // Data URIs and truly-relative paths still resolve directly.
    try {
      const raw = value[idx];
      const direct = imgUrl(raw);
      const isSameOrigin = direct.startsWith("data:")
        || direct.startsWith("/")
        || direct.startsWith(process.env.REACT_APP_BACKEND_URL || "__unset__");
      const fetchUrl = isSameOrigin
        ? direct
        : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/proxy?url=${encodeURIComponent(direct)}`;
      const token = localStorage.getItem("kb_token");
      const resp = await fetch(fetchUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        // Surface the backend's actual reason so operators can tell
        // "Host not on allow-list" vs "Not an image" vs a network
        // failure apart — instead of the previous generic toast.
        let detail = "";
        try {
          const body = await resp.clone().json();
          detail = body?.detail || "";
        } catch {
          detail = "";
        }
        throw new Error(detail || `Fetch ${resp.status}`);
      }
      const blob = await resp.blob();
      const ext = (blob.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
      const f = new File([blob], `existing-${idx}.${ext}`, { type: blob.type || "image/jpeg" });
      setRecropIndex(idx);
      setCropQueue([f]);
    } catch (err) {
      toast.error(err?.message
        ? `Could not load image for cropping: ${err.message}`
        : "Could not load image for cropping");
    }
  };

  const handleCropConfirm = async (croppedFile) => {
    // Pop the front of the queue and upload the cropped result.
    const remaining = cropQueue.slice(1);
    try {
      const url = await uploadOne(croppedFile);
      if (recropIndex !== null) {
        // Re-crop flow — replace in place, don't append.
        const next = [...value];
        next[recropIndex] = url;
        onChange(next);
        toast.success("Image re-cropped");
      } else {
        onChange([...value, url]);
        toast.success("Image uploaded");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    setCropQueue(remaining);
    if (remaining.length === 0) setRecropIndex(null);
  };

  const handleCropSkip = async () => {
    const [original, ...rest] = cropQueue;
    // In re-crop mode "Use original" is a no-op — the existing image is
    // already the original, so we just close the modal without a
    // useless duplicate upload.
    if (recropIndex !== null) {
      toast.info("Kept original — no re-crop applied");
      setCropQueue([]);
      setRecropIndex(null);
      return;
    }
    try {
      const url = await uploadOne(original);
      onChange([...value, url]);
      toast.success("Image uploaded (uncropped)");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    setCropQueue(rest);
  };

  const handleCropCancel = () => {
    setCropQueue([]);
    setRecropIndex(null);
  };

  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div data-testid={testid}>
      <div className={`grid ${max === 1 ? "grid-cols-1 max-w-xs" : "grid-cols-3 md:grid-cols-4"} gap-3`}>
        {value.map((src, i) => (
          <div key={i} className={`relative ${aspect} border border-[hsl(var(--kb-border))] overflow-hidden bg-[hsl(var(--kb-surface-alt))] group`}>
            <img src={imgUrl(src)} alt="" className="w-full h-full object-contain" />
            <button
              type="button"
              onClick={() => remove(i)}
              className="absolute top-1 right-1 bg-white p-1 shadow"
              data-testid={`${testid}-remove-${i}`}
              title="Remove image"
            >
              <X size={12} />
            </button>
            <button
              type="button"
              onClick={() => openCropperForExisting(i)}
              className="absolute top-1 left-1 bg-white p-1 shadow opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`${testid}-crop-${i}`}
              title="Re-crop this image"
            >
              <CropIcon size={12} />
            </button>
          </div>
        ))}
        {value.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className={`${aspect} border border-dashed border-[hsl(var(--kb-border))] flex flex-col items-center justify-center gap-2 text-xs uppercase tracking-wider text-[hsl(var(--kb-muted))] hover:bg-[hsl(var(--kb-surface-alt))]`}
            data-testid={`${testid}-add`}
            disabled={loading || cropQueue.length > 0}
          >
            <Upload size={16} />
            {loading ? "Uploading…" : cropQueue.length > 0 ? "Cropping…" : label}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onSelect}
      />
      {cropQueue.length > 0 && (
        <ImageCropper
          file={cropQueue[0]}
          aspect={cropAspect || 1}
          lockAspect={lockAspect && !!cropAspect}
          allowSkip={allowSkipCrop}
          onConfirm={handleCropConfirm}
          onSkip={handleCropSkip}
          onCancel={handleCropCancel}
          testid={`${testid}-cropper`}
        />
      )}
    </div>
  );
}
